<script setup lang="ts">
/**DataTableFacetedFilter - Para ScheduleCampaignDeliveries */
import type { Column } from '@tanstack/vue-table'
import type { Component } from 'vue'
import { computed } from 'vue'

// Usando os componentes shadcn
import { Badge } from 'dashboard/components-shadcn/new-york/ui/badge'
import { Button } from 'dashboard/components-shadcn/new-york/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator
} from 'dashboard/components-shadcn/new-york/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'dashboard/components-shadcn/new-york/ui/popover'
import { Separator } from 'dashboard/components-shadcn/new-york/ui/separator'

interface DataTableFacetedFilter {
  column?: Column<any, any>
  title?: string
  options: {
    label: string
    value: string
    icon?: Component
  }[]
}

const props = defineProps<DataTableFacetedFilter>()

const facets = computed(() => props.column?.getFacetedUniqueValues())
const selectedValues = computed(() => new Set(props.column?.getFilterValue() as string[]))

// Função para classes condicionais
const classNames = (...classes: (string | undefined | boolean)[]) => {
  return classes.filter(Boolean).join(' ')
}
</script>

<template>
  <Popover>
    <PopoverTrigger as-child>
      <Button
        variant="primary"
        size="sm"
        class="h-8 border-b border-n-slate-6  bg-n-slate-6 hover:bg-n-slate-4 dark:hover:bg-slate-4 transition-colors"
      >
        <span class="i-lucide-plus-circle mr-2 h-4 w-4 text-n-slate-11"></span>
        <span class="text-n-slate-12">{{ title }}</span>
        <template v-if="selectedValues.size > 0">
          <Separator orientation="vertical" class="mx-2 h-4 bg-n-slate-6" />
          <Badge
            variant="secondary"
            class="rounded-sm px-1 font-normal lg:hidden bg-n-iris-2 text-n-iris-11 border-n-iris-7"
          >
            {{ selectedValues.size }}
          </Badge>
          <div class="hidden space-x-1 lg:flex">
            <Badge
              v-if="selectedValues.size > 2"
              variant="secondary"
              class="rounded-sm px-1 font-normal bg-n-iris-2 text-n-iris-11 border-n-iris-7"
            >
              {{ selectedValues.size }} selecionado(s)
            </Badge>

            <template v-else>
              <Badge
                v-for="option in options
                  .filter((option) => selectedValues.has(option.value))"
                :key="option.value"
                variant="secondary"
                class="rounded-sm px-1 font-normal bg-n-iris-2 text-n-iris-11 border-n-iris-7"
              >
                {{ option.label }}
              </Badge>
            </template>
          </div>
        </template>
      </Button>
    </PopoverTrigger>

    <PopoverContent
      class="w-[220px] p-0 bg-n-slate-1 border-n-slate-6 shadow-lg rounded-lg overflow-hidden"
      align="start"
    >
      <div class="bg-n-slate-1">
        <Command class="bg-n-slate-1">
          <CommandList class="bg-n-slate-1 max-h-60 overflow-y-auto">
            <CommandEmpty class="py-6 text-center text-sm text-n-slate-10 bg-n-slate-1">
              Nenhum resultado encontrado.
            </CommandEmpty>

            <CommandGroup class="p-1 bg-n-slate-1">
              <CommandItem
                v-for="option in options"
                :key="option.value"
                :value="option"
                class="flex items-center px-2 py-2 text-sm bg-n-slate-1 hover:bg-n-slate-3 rounded-sm cursor-pointer transition-colors"
                @select="(e) => {
                  const isSelected = selectedValues.has(option.value)
                  if (isSelected) {
                    selectedValues.delete(option.value)
                  }
                  else {
                    selectedValues.add(option.value)
                  }
                  const filterValues = Array.from(selectedValues)
                  column?.setFilterValue(
                    filterValues.length ? filterValues : undefined,
                  )
                }"
              >
                <div
                  :class="classNames(
                    'mr-3 flex h-4 w-4 items-center justify-center rounded border transition-colors',
                    selectedValues.has(option.value)
                      ? 'bg-n-iris-9 border-n-iris-9 text-n-slate-1'
                      : 'border-n-slate-7 hover:border-n-slate-8',
                  )"
                >
                  <span
                    v-if="selectedValues.has(option.value)"
                    class="i-lucide-check h-3 w-3"
                  ></span>
                </div>

                <component
                  :is="option.icon"
                  v-if="option.icon"
                  class="mr-2 h-4 w-4 text-n-slate-10"
                />

                <span class="flex-1 text-n-slate-12">{{ option.label }}</span>

                <span
                  v-if="facets?.get(option.value)"
                  class="ml-auto text-xs text-n-slate-10 bg-n-slate-3 px-1.5 py-0.5 rounded font-mono"
                >
                  {{ facets.get(option.value) }}
                </span>
              </CommandItem>
            </CommandGroup>

            <template v-if="selectedValues.size > 0">
              <CommandSeparator class="bg-n-slate-4" />
              <CommandGroup class="p-1 bg-n-slate-1">
                <CommandItem
                  :value="{ label: 'Limpar filtros' }"
                  class="flex items-center justify-center px-2 py-2 text-sm text-red-500 hover:text-ruby-12 hover:bg-ruby-2 cursor-pointer transition-colors bg-n-slate-1"
                  @select="column?.setFilterValue(undefined)"
                >
                  <span class="i-lucide-x mr-2 h-3 w-3 text-red-500"></span>

                  Limpar filtros
                </CommandItem>
              </CommandGroup>
            </template>
          </CommandList>
        </Command>
      </div>
    </PopoverContent>
  </Popover>
</template>

<style scoped>
/* Garantir que as variáveis CSS do sistema funcionem */
:deep([data-radix-popover-content]) {
  background-color: rgb(var(--slate-1));
  border: 1px solid rgb(var(--slate-6));
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  border-radius: 0.5rem;
  padding: 0;
  overflow: hidden;
}

/* Estilos para o Command */
:deep([cmdk-root]) {
  background-color: rgb(var(--slate-1)) !important;
}

/* Lista de itens */
:deep([cmdk-list]) {
  background-color: rgb(var(--slate-1)) !important;
}

:deep([cmdk-group]) {
  background-color: rgb(var(--slate-1)) !important;
}

:deep([cmdk-item]) {
  background-color: rgb(var(--slate-1)) !important;
  color: rgb(var(--slate-12)) !important;
}

:deep([cmdk-item][data-selected="true"]) {
  background-color: rgb(var(--slate-3)) !important;
}

:deep([cmdk-empty]) {
  background-color: rgb(var(--slate-1)) !important;
  color: rgb(var(--slate-10)) !important;
}

/* Separator */
:deep([cmdk-separator]) {
  background-color: rgb(var(--slate-4)) !important;
  height: 1px !important;
  margin: 0.25rem 0 !important;
}

/* Remover outline padrão do Radix */
:deep([data-radix-popover-content]:focus-visible) {
  outline: none;
}

/* Smooth animations */
:deep([data-radix-popover-content][data-state="open"]) {
  animation: slideDownAndFade 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

:deep([data-radix-popover-content][data-state="closed"]) {
  animation: slideUpAndFade 150ms cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideDownAndFade {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUpAndFade {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-4px);
  }
}
</style>